<script lang="ts">
    import { setUser } from "../../../sentry"
    import ShippingDeliveryAddress from "./ShippingDeliveryAddress.svelte"
    import { maxInputLength } from "../../../../../api/hooks/config-client"
    import { spaNavigate } from "../../../functionTools/navigation"
    import { cart, checkoutData, getAsyncCartSum } from "../../../functionTools/cart"
    import { login } from "../../../functionTools/login"
    import { overlay } from "../../../store"
    import { location } from "../../../i18n"
    import { getCountry, postOrder, errorCodeFrom, errorMessageKeyFrom } from "../../../api"
    import { purchaseFromOrder } from "../../../lib/ga"
    import CheckoutErrorBox from "./CheckoutErrorBox.svelte"
    import { _ } from "svelte-i18n"
    import Cart from "../../ecommerce/cart/Cart.svelte"
    import FormInputText from "../../widgets/Form/FormInputText.svelte"
    import FormInputCheckbox from "../../widgets/Form/FormInputCheckbox.svelte"
    import BarLoader from "svelte-loading-spinners/BarLoader.svelte"
    import CheckoutFooter from "./CheckoutFooter.svelte"
    import { truncateFields } from "./helper"
    import Checkboxes from "./Checkboxes.svelte"

    let cancellationNotice = $state(false)
    let privacyNotice = $state(false)
    let conditionsNotice = $state(false)
    let loading = $state(false)
    let errorCode: string | null = $state(null)
    let errorKey = $state("checkout.error-order")

    let dropShipment = $state(false)

    location.subscribe((l) => {
        if (l?.path == "/checkout/dealer") {
            $overlay.show = false
        }
    })

    $effect(() => {
        if (!$cart?.length || !$login) {
            spaNavigate("/", { replace: true })
        }
    })

    const submitOrder = async () => {
        try {
            setUser({
                username:
                    $login?.account?.ident &&
                    $login.account.ident.dealerNumber + "-" + $login.account.ident.personNumber,
                email: $checkoutData.contact?.email,
            })

            $checkoutData.contact = truncateFields($checkoutData.contact, maxInputLength)
            $checkoutData.deliveryAddress = truncateFields($checkoutData.deliveryAddress, maxInputLength)
            $checkoutData.billingAddress = truncateFields($checkoutData.billingAddress, maxInputLength)

            const deliveryCountry = await getCountry(
                dropShipment ? $checkoutData.deliveryAddress?.countryCode || "DE" : "DE"
            )

            const orderBaseData: Order = {
                dealerNumber: $login?.account?.ident?.dealerNumber?.toString(),
                contact: $checkoutData.contact?.email ? { email: $checkoutData.contact.email } : undefined,
                deliveryCountry,
                commission: $checkoutData.commission ? $checkoutData.commission : undefined,
                payment: {
                    type: "invoice",
                },
                deliveryAddress: dropShipment ? $checkoutData.deliveryAddress : undefined,
                cart: [],
                sum: null,
            }

            const carts: CartItem[][] = [
                $cart.filter((i) => !i.product.price_net_dealer_is_fixed),
                $cart.filter((i) => i.product.price_net_dealer_is_fixed),
            ]

            for (let i = 0; i < carts.length; i++) {
                const cartItems = carts[i]

                if (cartItems.length) {
                    const created = await postOrder({
                        ...orderBaseData,
                        cart: cartItems,
                        sum: await getAsyncCartSum(cartItems),
                        packaging: cartItems[0].product?.price_net_dealer_is_fixed,
                        bonus: !cartItems[0].product?.price_net_dealer_is_fixed,
                    })
                    // GA4 purchase je Order in die Queue (eigene sequence pro Split)
                    purchaseFromOrder(created)
                }
            }

            typeof window !== "undefined" &&
                window.setTimeout(() => {
                    $cart = []
                }, 1000)

            spaNavigate("/checkout/confirmation")
        } catch (e) {
            console.log(e)
            errorCode = errorCodeFrom(e)
            errorKey = errorMessageKeyFrom(e)
            loading = false
            // Bewusst kein Re-Throw: Die Box ist die Fehlermeldung (siehe Overview).
            return
        }
    }

    function submitClick() {
        if (!loading) {
            loading = true
            return submitOrder()
        }
        return Promise.resolve()
    }

    let invalidProductsInCart = $state(false)
</script>

<div class="subline">{$_("checkout.order")}</div>
<h2 class="h2_nooffset">{$_("checkout.summary")}</h2>

<CheckoutErrorBox code={errorCode} messageKey={errorKey} />

<form
    onsubmit={(e) => {
        e.preventDefault()
        submitClick()
    }}
>
    <section>
        <div class="section_headline">{$_("checkout.cart")}</div>
        <Cart checkoutButton={false} bind:invalidProductsInCart />
    </section>

    {#if !invalidProductsInCart}
        <section>
            <div class="section_headline">
                {$_("checkout.delivery-and-billing-address")}
            </div>
            <div class="row">
                <div class="col-md-12">
                    <FormInputText
                        name="dealerNumber"
                        placeholder={$_("checkout.dealer-number")}
                        label={$_("checkout.dealer-number")}
                        disabled
                        value={$login?.account?.ident?.dealerNumber}
                    />
                </div>
            </div>
            <div class="row">
                <div class="col-md-12">
                    {@html $_("checkout.dealer-address-info-html")}
                </div>
            </div>
            <div class="row">
                <div class="col-md-12">
                    <FormInputCheckbox label={$_("checkout.drop-shipment-address")} bind:checked={dropShipment} />
                </div>
                {#if dropShipment}
                    <div class="col-md-12">
                        <ShippingDeliveryAddress hideHeadline />
                    </div>
                {/if}
            </div>
        </section>
        <section>
            <div class="section_headline">{$_("checkout.further-data")}</div>
            <div class="row">
                <div class="col-md-12">
                    {$_("checkout.dealer-email-prompt")}
                    <FormInputText
                        name="email"
                        label={$_("checkout.email")}
                        bind:value={$checkoutData.contact.email}
                        type="email"
                        maxlength={maxInputLength.email}
                    />
                </div>
                <div class="col-md-12">
                    {$_("checkout.dealer-commission-prompt")}
                    <FormInputText
                        name="commission"
                        label={$_("checkout.commission")}
                        bind:value={$checkoutData.commission}
                        type="text"
                        maxlength={maxInputLength.commission}
                    />
                </div>
            </div>
        </section>
        <section style="border-bottom:dotted 1px var(--border-color-2);">
            <div class="section_headline">{$_("checkout.payment")}</div>
            <p>{$_("checkout.invoice")}</p>
        </section>

        <section>
            <FormInputCheckbox label={$_("checkout.store-my-data")} bind:checked={$checkoutData.localStorage} />
            <Checkboxes bind:cancellationNotice bind:privacyNotice bind:conditionsNotice />
        </section>
        {#if loading}
            <BarLoader />
        {:else}
            <CheckoutFooter
                nexttitle={$_("checkout.order-now")}
                submitDisabled={!cancellationNotice || !privacyNotice || !conditionsNotice}
            />
        {/if}
    {/if}
</form>
